import { useState, useCallback } from "react";
import { Email, LTLShipment, LTLOffer } from "@src/types/api";
import { emailShipmentsLtl } from "@taskpane/utils/emailShipments";
import { useTaskpaneServices } from "@src/load-agent/app/TaskpaneServicesContext";
import {
  runLtlPriceRequest,
  runLtlSelectOffer,
  type PriceOptions,
  type PriceResult,
  type SelectOfferOptions,
} from "./ltlPricingActions";

interface UseLTLPricingOptions {
  emailData: Email | null;
  clickedLTLShipment: LTLShipment | null;
  setEmailData: (emailData: Email | null) => void;
  setClickedLTLShipment: (shipment: LTLShipment | null) => void;
}

export function useLTLPricing(options: UseLTLPricingOptions) {
  const { loadApp } = useTaskpaneServices();
  const { emailData, clickedLTLShipment, setEmailData, setClickedLTLShipment } = options;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const price = useCallback(
    async (opts: PriceOptions): Promise<PriceResult> => {
      setError(null);
      setLoading(true);
      try {
        return await runLtlPriceRequest(
          {
            loadApp,
            emailData,
            clickedLTLShipment,
            setEmailData,
            setClickedLTLShipment,
            setError,
          },
          opts
        );
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred";

        setError(errorMessage);
        return { success: false } as PriceResult;
      } finally {
        setLoading(false);
      }
    },
    [emailData, clickedLTLShipment, loadApp, setEmailData, setClickedLTLShipment]
  );
  const selectOffer = useCallback(
    async (opts: SelectOfferOptions): Promise<boolean> => {
      setError(null);
      try {
        await runLtlSelectOffer(loadApp, emailData, setEmailData, opts);
        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred";

        setError(errorMessage);
        return false;
      }
    },
    [emailData, loadApp, setEmailData]
  );

  return {
    price,
    selectOffer,
    loading,
    error,
  };
}

export function extractOffersFromEmail(email: Email | null, shipmentId: string): LTLOffer[] {
  if (!email) return [];
  const shipment = emailShipmentsLtl(email).find((s: LTLShipment) => s.shipmentId === shipmentId);

  return (shipment?.quotedRates ?? []) as LTLOffer[];
}
